import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { makeStyles } from "tss-react/mui";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { i18n } from "../../translate/i18n";
import SettingsCard from "../Settings/SettingsCard";
import WeeklyScheduleTable, { dayStatus, toWeek, weekHasErrors, weekIsRestricted } from "../WeeklyScheduleTable";
import QueueHoursDialog from "./QueueHoursDialog";

const t = (key, options) => i18n.t(`settings.attention.hours.${key}`, options);

const MODES = ["disabled", "company", "queue"];

const useStyles = makeStyles()((theme) => ({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  modeRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  modeText: {
    flex: 1,
    minWidth: 0,
  },
  modeTitle: {
    fontSize: 13.5,
    fontWeight: 600,
  },
  modeHelp: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
  },
  modeSelect: {
    width: 240,
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
  },
}));

const countStatus = (schedules, status) => toWeek(schedules).filter((day) => dayStatus(day) === status).length;

const BusinessHoursCard = () => {
  const { classes } = useStyles();
  const [hours, setHours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [week, setWeek] = useState(toWeek([]));
  const [savingMode, setSavingMode] = useState(false);
  const [savingWeek, setSavingWeek] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [editingQueue, setEditingQueue] = useState(null);

  const applyHours = (data) => {
    setHours(data);
    setWeek(toWeek(data.schedules));
    setShowErrors(false);
  };

  useEffect(() => {
    api
      .get("/business-hours")
      .then(({ data }) => applyHours(data))
      .catch(toastError)
      .finally(() => setLoading(false));
  }, []);

  const dirty = useMemo(
    () => hours && JSON.stringify(week) !== JSON.stringify(toWeek(hours.schedules)),
    [week, hours]
  );

  const changeMode = async (mode) => {
    setSavingMode(true);
    try {
      const { data } = await api.put("/business-hours", { mode });
      applyHours(data);
      toast.success(t("saved"));
    } catch (err) {
      toastError(err);
    }
    setSavingMode(false);
  };

  const saveWeek = async () => {
    if (weekHasErrors(week)) {
      setShowErrors(true);
      return;
    }
    setSavingWeek(true);
    try {
      const { data } = await api.put("/business-hours", { schedules: week });
      applyHours(data);
      toast.success(t("saved"));
    } catch (err) {
      toastError(err);
    }
    setSavingWeek(false);
  };

  const replaceQueue = (queue) =>
    setHours((prev) => ({ ...prev, queues: prev.queues.map((item) => (item.id === queue.id ? queue : item)) }));

  const mode = hours?.mode || "disabled";

  return (
    <SettingsCard icon={ScheduleOutlinedIcon} title={t("cardTitle")} subtitle={t("cardSubtitle")}>
      {loading ? (
        <Box display="flex" justifyContent="center" py={1}>
          <CircularProgress size={22} />
        </Box>
      ) : (
        <div className={classes.body}>
          <div className={classes.modeRow}>
            <div className={classes.modeText}>
              <Typography className={classes.modeTitle}>{t("mode.title")}</Typography>
              <Typography className={classes.modeHelp}>{t(`mode.help.${mode}`)}</Typography>
            </div>
            <TextField
              select
              size="small"
              className={classes.modeSelect}
              value={mode}
              disabled={savingMode}
              onChange={(e) => changeMode(e.target.value)}
            >
              {MODES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`mode.${value}`)}
                </MenuItem>
              ))}
            </TextField>
          </div>

          {mode === "company" && (
            <>
              <WeeklyScheduleTable value={week} onChange={setWeek} />
              {showErrors && weekHasErrors(week) && <Alert severity="error">{t("fixErrors")}</Alert>}
              <Alert severity="info" variant="outlined">
                {t("companyMessageHint")}
              </Alert>
              <div className={classes.footer}>
                {dirty && <Chip size="small" color="warning" label={t("unsaved")} />}
                <Button variant="contained" onClick={saveWeek} disabled={!dirty || savingWeek}>
                  {t("save")}
                </Button>
              </div>
            </>
          )}

          {mode === "queue" && (
            <>
              <Alert severity="info" variant="outlined">
                {t("queueHint")}
              </Alert>
              {hours.queues.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("noQueues")}
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("queueTable.queue")}</TableCell>
                      <TableCell align="center">{t("queueTable.days")}</TableCell>
                      <TableCell align="center">{t("queueTable.message")}</TableCell>
                      <TableCell align="right" />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {hours.queues.map((queue) => {
                      const restricted = weekIsRestricted(toWeek(queue.schedules));
                      const missingMessage = restricted && !queue.outOfHoursMessage;
                      return (
                        <TableRow key={queue.id} hover>
                          <TableCell>{queue.name}</TableCell>
                          <TableCell align="center">
                            {restricted
                              ? t("queueTable.summary", {
                                  hours: countStatus(queue.schedules, "hours"),
                                  closed: countStatus(queue.schedules, "closed"),
                                })
                              : t("queueTable.noSchedule")}
                          </TableCell>
                          <TableCell align="center">
                            {missingMessage ? (
                              <Chip size="small" color="warning" label={t("queueTable.missing")} />
                            ) : queue.outOfHoursMessage ? (
                              t("queueTable.yes")
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => setEditingQueue(queue)}>
                              {t("edit")}
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </div>
      )}

      <QueueHoursDialog queue={editingQueue} onClose={() => setEditingQueue(null)} onSaved={replaceQueue} />
    </SettingsCard>
  );
};

export default BusinessHoursCard;
